import React, { useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import AppRouter from './AppRouter';
import {
    FeatureFlagsProvider,
    IndeedMessagingSdkProvider,
    IndeedMessagingThemeProvider,
    LocaleProvider,
    Env,
    MessagingSdk,
    PageConfig,
    PartialFeatureFlags,
    ConversationParticipantRole,
} from '@indeed/messaging-react';
import MainContent from '../components/MainContent/MainContent';
import { ProctorTestNames, ProctorCommonGroupValues } from 'proctor';
import { isInProctorGroup } from 'utils/proctorUtils';
import useLoggingFunctions from '../hooks/useLoggingFunctions';
import { getBrowserLogger } from '../../browser-logger';
import { PageEventType } from '../../browser-logger/types';

export type UserAgent = {
    isMobile: boolean;
    isNativeApp: boolean;
};

interface MockableAppProps {
    pageConfig: PageConfig & {
        env: string;
        userAgent: UserAgent;
    };
    sdk: MessagingSdk;
    flags?: PartialFeatureFlags;
}

interface AppProps {
    pageConfig: PageConfig & {
        env: string;
        userAgent: UserAgent;
    };
}

const MockableApp = ({ pageConfig, sdk, flags }: MockableAppProps) => {
    return (
        <FeatureFlagsProvider flags={flags}>
            <IndeedMessagingSdkProvider sdk={sdk}>
                {/* In IFL7, this provider adds a div. We need to add the flex styles here to properly provide height to our main section. */}
                <IndeedMessagingThemeProvider
                    useIFL7Styling={true}
                    sx={{
                        display: 'flex',
                        flexGrow: 1,
                        flexDirection: 'column',
                    }}
                >
                    <MainContent>
                        <LocaleProvider
                            locale={{
                                language: pageConfig.lang || 'en',
                                country: pageConfig.country || 'US',
                            }}
                        >
                            <AppRouter userAgent={pageConfig.userAgent} />
                        </LocaleProvider>
                    </MainContent>
                </IndeedMessagingThemeProvider>
            </IndeedMessagingSdkProvider>
        </FeatureFlagsProvider>
    );
};

const App = ({ pageConfig }: AppProps) => {
    const loggingFunctions = useLoggingFunctions();

    useEffect(() => {
        try {
            getBrowserLogger().logPageEvent(PageEventType.INITIAL_BUNDLE_PING);
        } catch {
            // Browser logger may not be initialized yet
        }
    }, []);

    const isAiMessagingRewriteEnabled = isInProctorGroup(
        ProctorTestNames.AIMS_AI_ASSISTED_REWRITE_ONLY,
        ProctorCommonGroupValues.ACTIVE
    );

    const isAiMessagingFollowUpPromptEnabled = isInProctorGroup(
        ProctorTestNames.AI_MESSAGING_FOLLOW_UP_PROMPT,
        ProctorCommonGroupValues.ACTIVE
    );

    const isAiMessagingVersionTwoEnabled = isInProctorGroup(
        ProctorTestNames.AIMS_JS_AI_MESSAGING_VERSION_TWO,
        1
    );

    const isAiMessagingVersionTwoMultipleOptionsEnabled = isInProctorGroup(
        ProctorTestNames.AIMS_JS_AI_MESSAGING_VERSION_TWO,
        2
    );

    const isDisplayConversationTitleEnabled = isInProctorGroup(
        ProctorTestNames.IMS_JS_DISPLAY_CONVERSATION_TITLE,
        ProctorCommonGroupValues.ACTIVE
    );

    const isSendCorrectnessEnabled = isInProctorGroup(
        ProctorTestNames.MESSAGING_SEND_CORRELATION_ENABLED,
        ProctorCommonGroupValues.ACTIVE
    );

    const isHorizontalAttachmentsEnabled = isInProctorGroup(
        ProctorTestNames.AMPL_JS_HORIZONTAL_ATTACHMENTS,
        ProctorCommonGroupValues.ACTIVE
    );

    const flags: PartialFeatureFlags = {
        onlineStatus: { enabled: true },
        glassdoorOaopEnabled: true,
        aiMessaging: {
            jobseeker: {
                rewriteEnabled: isAiMessagingRewriteEnabled || isAiMessagingVersionTwoEnabled,
                followUpPromptEnabled: isAiMessagingFollowUpPromptEnabled,
                v2Enabled: isAiMessagingVersionTwoEnabled,
                v2ShowMultipleOptions: isAiMessagingVersionTwoMultipleOptionsEnabled,
            },
        },
        backendLockTextEnabled: true,
        jobseekerDisplayConversationTitle: isDisplayConversationTitleEnabled,
        horizontalAttachmentsEnabled: isHorizontalAttachmentsEnabled,
    };

    const sdk = new MessagingSdk({
        env:
            // Prod just doesn't have an env value
            pageConfig.env === 'qa' || pageConfig.env === 'local' ? Env.QA : Env.Prod,
        pageConfig,
        sdkConfig: {
            userRole: ConversationParticipantRole.JobSeeker,
            loggingFunctions,
            uploadUrlConfig: undefined,
            sendCorrectnessEnabled: isSendCorrectnessEnabled,
        },
    });

    return (
        <Router>
            <MockableApp pageConfig={pageConfig} sdk={sdk} flags={flags} />
        </Router>
    );
};

export default App;
export { MockableApp, MockableAppProps };
